import { useState, useMemo, useRef } from "react"
import { usePage, useForm, router } from "@inertiajs/react"
import {
  Plus, Trash2, ExternalLink, Check,
  Cloud, Terminal, ShoppingCart, Brain, Wallet, Heart, Leaf,
  Mic, Store, Building2, Layers, TrendingUp, GitBranch,
  Landmark, Briefcase, FolderOpen,
} from "lucide-react"

import Layout from "@/pages/layouts/application"
import { PageHeader } from "@/components/core/page_header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"

import * as api from "@/api"

const TOPIC_META = {
  saas: { label: "SaaS", icon: Cloud },
  devtools: { label: "Dev Tools", icon: Terminal },
  ecommerce: { label: "E-Commerce & DTC", icon: ShoppingCart },
  ai_ml: { label: "AI & Machine Learning", icon: Brain },
  fintech: { label: "Fintech & Payments", icon: Wallet },
  healthtech: { label: "Health & Biotech", icon: Heart },
  climate_tech: { label: "Climate & Energy", icon: Leaf },
  creator_economy: { label: "Creator Economy", icon: Mic },
  marketplaces: { label: "Marketplaces & Platforms", icon: Store },
  enterprise_sales: { label: "Enterprise & B2B Sales", icon: Building2 },
  product_management: { label: "Product & Design", icon: Layers },
  growth_marketing: { label: "Growth & Marketing", icon: TrendingUp },
  open_source: { label: "Open Source", icon: GitBranch },
  venture_capital: { label: "Venture Capital", icon: Landmark },
  agency_consulting: { label: "Agency & Consulting", icon: Briefcase },
}

const KIND_LABELS = {
  substack: "Substack",
  linkedin_account: "LinkedIn",
  hn_topic: "HN topic",
  reddit_community: "Reddit",
  blog: "Blog",
  newsletter: "Newsletter",
}

function TopicPicker({ orgNanoId, catalog, selected }) {
  const [draft, setDraft] = useState(selected)
  const debounceRef = useRef(null)

  const persist = (next) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      router.patch(
        api.organizationsMediaSources.updateTopics.path({ org_nano_id: orgNanoId }),
        { topics: next },
        { preserveScroll: true, preserveState: true }
      )
    }, 400)
  }

  const toggle = (slug) => {
    const next = draft.includes(slug)
      ? draft.filter((s) => s !== slug)
      : [...draft, slug]
    setDraft(next)
    persist(next)
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Topics</h2>
        <p className="text-sm text-muted-foreground">
          Select topics that match what you want to post about. New topics add their curated sources — deselecting a topic leaves existing sources intact.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {catalog.map((slug) => {
          const meta = TOPIC_META[slug] || { label: slug, icon: FolderOpen }
          const Icon = meta.icon
          const isSelected = draft.includes(slug)

          return (
            <button
              key={slug}
              type="button"
              onClick={() => toggle(slug)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
                isSelected
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border/60 bg-card text-foreground/70 hover:border-border hover:bg-accent/50"
              }`}
            >
              {isSelected ? (
                <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
              ) : (
                <Icon className="h-3.5 w-3.5 shrink-0" />
              )}
              {meta.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function SourceRow({ source, orgNanoId, kinds }) {
  const [editing, setEditing] = useState(false)

  const { data, setData, patch, processing, errors } = useForm({
    name: source.name,
    kind: source.kind,
    url: source.url,
    relevance_note: source.relevance_note || "",
  })

  const save = (e) => {
    e.preventDefault()
    patch(
      api.organizationsMediaSources.update.path({
        org_nano_id: orgNanoId,
        id: source.id,
      }),
      {
        preserveScroll: true,
        onSuccess: () => setEditing(false),
      }
    )
  }

  const toggleActive = (next) => {
    router.patch(
      api.organizationsMediaSources.update.path({
        org_nano_id: orgNanoId,
        id: source.id,
      }),
      { media_source: { active: next } },
      { preserveScroll: true }
    )
  }

  const remove = () => {
    if (!confirm(`Remove "${source.name}"?`)) return
    router.delete(
      api.organizationsMediaSources.destroy.path({
        org_nano_id: orgNanoId,
        id: source.id,
      }),
      { preserveScroll: true }
    )
  }

  if (editing) {
    return (
      <form onSubmit={save} className="p-3 border rounded-md space-y-3 bg-background">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor={`name-${source.id}`}>Name</Label>
            <Input
              id={`name-${source.id}`}
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor={`kind-${source.id}`}>Kind</Label>
            <Select value={data.kind} onValueChange={(v) => setData("kind", v)}>
              <SelectTrigger id={`kind-${source.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {kinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {KIND_LABELS[k] || k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`url-${source.id}`}>URL</Label>
          <Input
            id={`url-${source.id}`}
            value={data.url}
            onChange={(e) => setData("url", e.target.value)}
          />
          {errors.url && <p className="text-xs text-destructive">{errors.url}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor={`note-${source.id}`}>Relevance note</Label>
          <Textarea
            id={`note-${source.id}`}
            rows={2}
            value={data.relevance_note}
            onChange={(e) => setData("relevance_note", e.target.value)}
            placeholder="Why this source is relevant (optional)"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={processing}>
            {processing ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <div className="flex items-center gap-3 p-3 border rounded-md bg-background">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{source.name}</span>
          <Badge variant="secondary">{KIND_LABELS[source.kind] || source.kind}</Badge>
        </div>
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mt-1"
        >
          <span className="truncate max-w-md">{source.url}</span>
          <ExternalLink className="size-3 shrink-0" />
        </a>
        {source.relevance_note && (
          <p className="text-xs text-muted-foreground mt-1">{source.relevance_note}</p>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-2">
          <Switch checked={source.active} onCheckedChange={toggleActive} />
          <span className="text-xs text-muted-foreground">
            {source.active ? "Active" : "Paused"}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button variant="ghost" size="icon" onClick={remove} aria-label="Remove source">
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  )
}

function NewSourceForm({ orgNanoId, kinds, open, setOpen }) {
  const { data, setData, post, processing, errors, reset } = useForm({
    name: "",
    kind: kinds[0],
    url: "",
    relevance_note: "",
    active: true,
  })

  const submit = (e) => {
    e.preventDefault()
    post(
      api.organizationsMediaSources.create.path({ org_nano_id: orgNanoId }),
      {
        preserveScroll: true,
        onSuccess: () => {
          reset()
          setOpen(false)
        },
      }
    )
  }

  if (!open) return null

  return (
    <form onSubmit={submit} className="p-4 border rounded-md space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="new-name">Name</Label>
          <Input
            id="new-name"
            value={data.name}
            onChange={(e) => setData("name", e.target.value)}
            placeholder="e.g. Stratechery"
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor="new-kind">Kind</Label>
          <Select value={data.kind} onValueChange={(v) => setData("kind", v)}>
            <SelectTrigger id="new-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {kinds.map((k) => (
                <SelectItem key={k} value={k}>
                  {KIND_LABELS[k] || k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="new-url">URL</Label>
        <Input
          id="new-url"
          value={data.url}
          onChange={(e) => setData("url", e.target.value)}
          placeholder="https://..."
        />
        {errors.url && <p className="text-xs text-destructive">{errors.url}</p>}
      </div>

      <div className="space-y-1">
        <Label htmlFor="new-note">Relevance note</Label>
        <Textarea
          id="new-note"
          rows={2}
          value={data.relevance_note}
          onChange={(e) => setData("relevance_note", e.target.value)}
          placeholder="Why this source is relevant (optional)"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            reset()
            setOpen(false)
          }}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={processing}>
          {processing ? "Adding..." : "Add source"}
        </Button>
      </div>
    </form>
  )
}

function TopicSection({ topicSlug, sources, orgNanoId, kinds }) {
  const meta = TOPIC_META[topicSlug] || { label: topicSlug, icon: FolderOpen }
  const Icon = meta.icon
  const activeCount = sources.filter((s) => s.active).length

  return (
    <AccordionItem value={topicSlug} className="border rounded-md bg-card border-b">
      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
        <div className="flex items-center gap-3 flex-1 pr-8">
          <Icon className="size-4 shrink-0" />
          <span className="font-medium">{meta.label}</span>
          <Badge variant="secondary" className="ml-auto">
            {activeCount} / {sources.length}
          </Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No sources yet for this topic.
          </p>
        ) : (
          <div className="space-y-2">
            {sources.map((source) => (
              <SourceRow
                key={source.id}
                source={source}
                orgNanoId={orgNanoId}
                kinds={kinds}
              />
            ))}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  )
}

export default function Index({
  media_sources,
  kinds,
  topics_catalog,
  selected_topics,
  topic_assignments,
}) {
  const { organization } = usePage().props.auth
  const [addSourceOpen, setAddSourceOpen] = useState(false)

  const grouped = useMemo(() => {
    const byTopic = Object.fromEntries(selected_topics.map((slug) => [slug, []]))
    const other = []

    media_sources.forEach((source) => {
      const topic = topic_assignments[source.id]
      if (topic && byTopic[topic]) {
        byTopic[topic].push(source)
      } else {
        other.push(source)
      }
    })

    return { byTopic, other }
  }, [media_sources, selected_topics, topic_assignments])

  return (
    <Layout>
      <PageHeader
        title="Content Sources"
        description="Topics that shape the pool of sources we monitor for you."
      />

      <div className="space-y-8 mt-6">
        <TopicPicker
          orgNanoId={organization.nano_id}
          catalog={topics_catalog}
          selected={selected_topics}
        />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Sources</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddSourceOpen((v) => !v)}
            >
              <Plus className="size-4 mr-1" />
              Add custom source
            </Button>
          </div>

          <NewSourceForm
            orgNanoId={organization.nano_id}
            kinds={kinds}
            open={addSourceOpen}
            setOpen={setAddSourceOpen}
          />

          {selected_topics.length === 0 && grouped.other.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Select a topic above to populate sources, or add one manually below.
            </p>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {selected_topics.map((slug) => (
                <TopicSection
                  key={slug}
                  topicSlug={slug}
                  sources={grouped.byTopic[slug] || []}
                  orgNanoId={organization.nano_id}
                  kinds={kinds}
                />
              ))}

              {grouped.other.length > 0 && (
                <AccordionItem value="other" className="border rounded-md bg-card border-b">
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
                    <div className="flex items-center gap-3 flex-1 pr-8">
                      <FolderOpen className="size-4 shrink-0" />
                      <span className="font-medium">Custom & other</span>
                      <Badge variant="secondary" className="ml-auto">
                        {grouped.other.filter((s) => s.active).length} / {grouped.other.length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-2">
                      {grouped.other.map((source) => (
                        <SourceRow
                          key={source.id}
                          source={source}
                          orgNanoId={organization.nano_id}
                          kinds={kinds}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          )}
        </div>
      </div>
    </Layout>
  )
}
